/**
 * Photos on the Finish sheet. On a phone the tile opens the camera (`capture="environment"`); on a computer
 * it opens the file picker and the strip takes dropped files. Each photo is made ≤ 2000 px JPEG on the
 * device; the sheet decides when it is sent (the Finish sheet's After photos as soon as they are taken, the others
 * with the sheet's own press). "Straighten" finds a page's corners (or lets the person place them) and flattens it;
 * the straightened photo replaces the item as a new photo (new id, no folder path).
 */
import { useId, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { autoCorners, insetQuad, prepare, straighten, type Quad } from '@/lib/image';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

export interface PhotoItem {
  id: string;
  /** local: on this device, sent with Record. filed: already in the visit's folder (an edit). */
  kind: 'local' | 'filed';
  blob?: Blob;
  canvas?: HTMLCanvasElement;
  url?: string;
  path?: string;
}

const coarse = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
let seq = 0;

/** 15 Oct 2026: what went wrong on a phone reaches the app's log (no photo, no names — the kind of file and the error). */
export function reportClientProblem(kind: string, detail: Record<string, unknown>): void {
  try {
    void fetch('/api/diag/client', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, detail, ua: navigator.userAgent.slice(0, 200) }) }).catch(() => undefined);
  } catch {
    /* never in the way */
  }
}

export function PhotoPicker({
  id,
  items,
  onChange,
  legend,
  help,
  testid,
  trouble,
  onRetry,
  pickedWords,
}: {
  id: string;
  items: PhotoItem[];
  onChange: (next: PhotoItem[]) => void;
  legend: string;
  help?: React.ReactNode;
  testid: string;
  /** 15 Oct 2026: a line the sheet wants said under the photos (a photo that could not be sent yet). */
  trouble?: string;
  /** 17 Oct 2026: with `trouble`, a "Try again" press under it — the sheet sends the photos that did not upload again. */
  onRetry?: () => void;
  /**
   * 17 Oct 2026: what is said when photos are picked. Left out: "n photos added." A sheet that sends the photos itself
   * passes its own words — "sending", or "ready, sent with Record" — and says "added" only when the server took them.
   */
  pickedWords?: (count: number) => string;
}) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState<PhotoItem | null>(null);
  /** 15 Oct 2026: a photo this phone could not read is said on the sheet, not only to a screen reader. */
  const [failed, setFailed] = useState(0);
  const helpId = useId();

  const add = async (files: FileList | File[]) => {
    // 15 Oct 2026: some phones hand the camera's photo over with no type at all — it is still a photo
    const list = [...files].filter((f) => !f.type || f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name));
    if (!list.length) return;
    setBusy(true);
    setFailed(0);
    const added: PhotoItem[] = [];
    let bad = 0;
    for (const f of list) {
      try {
        const { canvas, blob } = await prepare(f);
        added.push({ id: `p${++seq}`, kind: 'local', blob, canvas, url: URL.createObjectURL(blob) });
      } catch (err) {
        // the phone could not draw it: a JPEG / PNG / WebP still goes as it is (the server takes it up to 8 MB)
        if (/^image\/(jpeg|png|webp)$/i.test(f.type) && f.size <= 8 * 1024 * 1024) {
          added.push({ id: `p${++seq}`, kind: 'local', blob: f, url: URL.createObjectURL(f) });
        } else {
          bad++;
          reportClientProblem('photo-read', { type: f.type || '(none)', size: f.size, message: err instanceof Error ? err.message : String(err) });
        }
      }
    }
    setBusy(false);
    if (bad) {
      setFailed(bad);
      announce(t('finish.photoFailed'), 'assertive');
    }
    if (added.length) {
      onChange([...items, ...added]);
      announce(pickedWords ? pickedWords(added.length) : t('finish.photoAdded', { count: added.length }));
    }
  };

  const remove = (p: PhotoItem) => {
    if (p.url) URL.revokeObjectURL(p.url);
    onChange(items.filter((x) => x.id !== p.id));
    announce(t('finish.photoRemoved'));
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files?.length) void add(e.dataTransfer.files);
  };

  return (
    <fieldset className="field" data-testid={testid}>
      <legend className="eyebrow">{legend}</legend>
      <div
        className={`photos${over ? ' is-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        {items.map((p, i) => (
          <div className="ph-item" key={p.id}>
            <div className="ph">{p.url ? <img src={p.url} alt={t('finish.photoN', { n: i + 1 })} /> : <Icon name="camera" label={t('finish.photoFiled', { n: i + 1 })} />}</div>
            {p.kind === 'local' ? (
              <div className="ph-actions">
                <button type="button" className="linkish" onClick={() => setFixing(p)} aria-label={t('finish.straightenPhoto', { n: i + 1 })}>
                  {t('finish.straighten')}
                </button>
                <button type="button" className="linkish" onClick={() => remove(p)} aria-label={t('finish.removePhoto', { n: i + 1 })}>
                  <Icon name="x" width={14} height={14} />
                </button>
              </div>
            ) : null}
          </div>
        ))}
        <label className="ph ph-add" htmlFor={id} title={t('finish.addPhoto')}>
          <Icon name="camera" width={26} height={26} />
          <span className="sr-only">{t('finish.addPhoto')}</span>
        </label>
        <input
          id={id}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          aria-describedby={helpId}
          data-testid={`${testid}-input`}
          {...(coarse() ? { capture: 'environment' as const } : {})}
          onChange={(e) => {
            if (e.target.files) void add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      <p className="help" id={helpId}>
        {help}
        {help ? ' · ' : ''}
        {over ? t('finish.dropHere') : t('finish.addPhotoHelp')}
        {busy ? ` · ${t('app.loading')}` : ''}
      </p>
      {trouble ? (
        <p className="warn" role="status" data-testid={`${testid}-trouble`}>
          {trouble}
        </p>
      ) : null}
      {trouble && onRetry ? (
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={onRetry} data-testid={`${testid}-retry`}>
            {t('finish.photoTryAgain')}
          </Button>
        </div>
      ) : null}
      {failed ? (
        <p className="warn" role="alert" data-testid={`${testid}-failed`}>
          {t('finish.photoFailedVisible', { count: failed })}
        </p>
      ) : null}
      {fixing?.canvas ? (
        <Straighten
          photo={fixing}
          onClose={() => setFixing(null)}
          onApply={(next) => {
            if (fixing.url) URL.revokeObjectURL(fixing.url);
            // 18 Oct 2026 (Eli): the straightened photo is a new photo — a new id and no folder path, so a sheet that
            // already sent the original sends this one as a new file beside it, and the original's path leaves the list
            onChange(items.map((x) => (x.id === fixing.id ? { ...next, id: `p${++seq}`, kind: 'local', path: undefined } : x)));
            setFixing(null);
            announce(t('finish.straightened'));
          }}
        />
      ) : null}
    </fieldset>
  );
}

const CORNERS = ['tl', 'tr', 'br', 'bl'] as const;

function Straighten({ photo, onClose, onApply }: { photo: PhotoItem; onClose: () => void; onApply: (p: PhotoItem) => void }) {
  const { t } = useTranslation();
  const canvas = photo.canvas!;
  const W = canvas.width;
  const H = canvas.height;
  const detected = useRef<Quad | null>(autoCorners(canvas));
  const [q, setQ] = useState<Quad>(detected.current ?? insetQuad(W, H));
  const [drag, setDrag] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const move = (i: number, x: number, y: number) =>
    setQ((cur) => cur.map((p, j) => (j === i ? { x: Math.min(W, Math.max(0, x)), y: Math.min(H, Math.max(0, y)) } : p)) as Quad);

  const pointer = (e: PointerEvent) => {
    if (drag === null || !box.current) return;
    const r = box.current.getBoundingClientRect();
    move(drag, ((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H);
  };

  const key = (i: number) => (e: KeyboardEvent) => {
    const step = (e.shiftKey ? 0.05 : 0.01) * Math.max(W, H);
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    move(i, q[i]!.x + d[0]!, q[i]!.y + d[1]!);
  };

  const pct = (v: number, of: number) => Math.round((v / of) * 100);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="straighten-dialog" data-testid="straighten">
        <DialogTitle>{t('finish.straightenTitle')}</DialogTitle>
        <DialogDescription>{detected.current ? t('finish.straightenHelp') : `${t('finish.straightenNone')} ${t('finish.straightenHelp')}`}</DialogDescription>
        <div
          className="straighten-box"
          ref={box}
          style={{ aspectRatio: `${W} / ${H}` }}
          onPointerMove={pointer}
          onPointerUp={() => setDrag(null)}
          onPointerLeave={() => setDrag(null)}
        >
          <img src={photo.url} alt="" draggable={false} />
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
            <polygon points={q.map((p) => `${p.x},${p.y}`).join(' ')} />
          </svg>
          {q.map((p, i) => (
            <button
              key={CORNERS[i]}
              type="button"
              className="corner"
              style={{ left: `${(p.x / W) * 100}%`, top: `${(p.y / H) * 100}%` }}
              aria-label={t('finish.cornerAt', { name: t(`finish.corner.${CORNERS[i]}`), x: pct(p.x, W), y: pct(p.y, H) })}
              onPointerDown={(e) => {
                (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                setDrag(i);
              }}
              onKeyDown={key(i)}
            />
          ))}
        </div>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Button type="button" variant="ghost" onClick={() => setQ(autoCorners(canvas) ?? insetQuad(W, H))}>
            {t('finish.resetCorners')}
          </Button>
          <div className="row" style={{ gap: 8 }}>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('book.cancel')}
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const out = await straighten(canvas, q);
                  onApply({ ...photo, canvas: out.canvas, blob: out.blob, url: URL.createObjectURL(out.blob) });
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('finish.apply')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
